import { CoralogixLogSeverity, CoralogixRum } from '@coralogix/browser';
import { webStorageUtil } from 'state/webStorage.state.service';
import { ELocalStorageKeys } from '../constants/enums';
import { isAxiosError } from 'axios';
import { useCallback, useEffect, useRef } from 'react';
import { isProdEnv } from 'utils/env';
import { getEnvVariable } from '@appcharge/shared-ui';

export default function useCoralogixMonitoring() {
  const healthCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const sessionStartTime = useRef<number>(Date.now());

  // Health check function
  const sendHealthLog = (type: 'startup' | 'periodic' | 'shutdown' = 'periodic') => {
    try {
      if (!isProdEnv()) {
        return;
      }
      const playerData = webStorageUtil.get(ELocalStorageKeys.PLAYER_DATA);
      const sessionToken = webStorageUtil.get(ELocalStorageKeys.SESSION_TOKEN);
      const sessionDuration = Date.now() - sessionStartTime.current;

      const healthData = {
        type: 'health_check',
        healthType: type,
        sessionDuration: Math.round(sessionDuration / 1000), // in seconds
        isLoggedIn: !!sessionToken,
        playerId: playerData?.playerId || 'anonymous',
        playerCountry: playerData?.playerCountry || 'unknown',
        environment: getEnvVariable('REACT_APP_ENV', process.env.REACT_APP_ENV) || 'unknown',
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        memoryUsage: (performance as any).memory ? {
          usedJSHeapSize: Math.round((performance as any).memory.usedJSHeapSize / 1024 / 1024), // MB
          totalJSHeapSize: Math.round((performance as any).memory.totalJSHeapSize / 1024 / 1024), // MB
          jsHeapSizeLimit: Math.round((performance as any).memory.jsHeapSizeLimit / 1024 / 1024), // MB
        } : null,
        performance: {
          navigationStart: performance.timing?.navigationStart || 0,
          loadEventEnd: performance.timing?.loadEventEnd || 0,
          domContentLoadedEventEnd: performance.timing?.domContentLoadedEventEnd || 0,
        },
      };

      CoralogixRum.log(CoralogixLogSeverity.Info, `Health check: ${type}`, healthData);

      if (process.env.NODE_ENV === 'development') {
        console.log(`Health log sent: ${type}`, healthData);
      }
    } catch (error) {
      console.error('Failed to send health log:', error);
    }
  };

  // Start periodic health checks
  const startHealthMonitoring = () => {
    if (healthCheckIntervalRef.current) {
      clearInterval(healthCheckIntervalRef.current);
    }

    // Send initial health log
    sendHealthLog('startup');

    // Set up periodic health checks (every 5 minutes)
    healthCheckIntervalRef.current = setInterval(() => {
      sendHealthLog('periodic');
    }, 5 * 60 * 1000); // 5 minutes
  };

  // Stop health monitoring
  const stopHealthMonitoring = () => {
    if (healthCheckIntervalRef.current) {
      clearInterval(healthCheckIntervalRef.current);
      healthCheckIntervalRef.current = null;
    }
    sendHealthLog('shutdown');
  };

  // Cleanup on unmount
  useEffect(() => {
    startHealthMonitoring();

    return () => {
      stopHealthMonitoring();
    };
  }, []);


  const logErrorToCoralogix = async (
    error: any,
    additionalAttributes: any = {},
    forceLog: boolean = false,
    customMessage: string = 'Unknown error'
  ) => {
    try {
      // Check if Coralogix is available
      if (typeof CoralogixRum === 'undefined' || !CoralogixRum.log) {
        console.warn('Coralogix RUM not available, skipping log');
        return;
      }

      // Temporarily increase sampling rate for debugging
      const shouldLog = Math.random() < 0.5 || forceLog; // Changed from 0.05 to 0.5
      if (!shouldLog) return;

      const playerData = webStorageUtil.get(ELocalStorageKeys.PLAYER_DATA);

      let errorDetails: any = { message: customMessage || 'Unknown error' };

      if (isAxiosError(error)) {
        errorDetails = {
          message: String(error?.message || 'Unknown error'),
          url: error?.config?.url || 'Unknown URL',
          method: error?.config?.method || 'Unknown Method',
          requestHeaders: error?.config?.headers
            ? JSON.stringify(error?.config?.headers)
            : 'No Request Headers',
          requestData: error?.config?.data
            ? JSON.stringify(error?.config?.data)
            : 'No Request Data',
        };

        if (error?.response) {
          errorDetails = {
            ...errorDetails,
            status: error?.response?.status || 'Unknown Status',
            statusText: error?.response?.statusText || 'Unknown Status Text',
            responseData: error?.response?.data
              ? JSON.stringify(error?.response?.data)
              : 'No Response Data',
          };
        }
      } else {
        errorDetails = {
          message: customMessage || String(error?.message || error?.toString() || 'Unknown error'),
          details: JSON.stringify(error),
        };
      }

      const customAttributes = {
        playerCountry: playerData?.playerCountry || 'Unknown Country',
        playerId: playerData?.playerId || 'Unknown ID',
        playerName: playerData?.playerName || 'Unknown Name',
        hasTrace: true,
        level: forceLog ? 'info' : 'error',
        environment: getEnvVariable('REACT_APP_ENV', process.env.REACT_APP_ENV) || 'Unknown Environment',
        timestamp: new Date().toISOString(),
        ...additionalAttributes,
      };

      if (forceLog) {
        CoralogixRum.log(CoralogixLogSeverity.Info, errorDetails.message, {
          ...customAttributes,
          ...errorDetails,
        });
      } else {
        // Log as error for actual errors
        CoralogixRum.error(errorDetails.message, {
          ...customAttributes,
          ...errorDetails,
        });
      }

    } catch (logError) {
      console.error('Failed to log error to Coralogix:', logError);
    }
  };

  const testCoralogixConnection = () => {
    try {
      if (!isProdEnv()) {
        return;
      }
      if (typeof CoralogixRum === 'undefined' || !CoralogixRum.log) {
        console.error('Coralogix RUM not available');
        return false;
      }

      // Send multiple test logs with different levels
      const testData = {
        test: true,
        timestamp: new Date().toISOString(),
        environment: getEnvVariable('REACT_APP_ENV', process.env.REACT_APP_ENV) || 'unknown',
        userAgent: navigator.userAgent,
        url: window.location.href,
        sessionId: Date.now().toString(),
      };

      // Test info log
      CoralogixRum.log(CoralogixLogSeverity.Info, 'Test info log from store-frontend', testData);

      // Test warning log
      CoralogixRum.log(CoralogixLogSeverity.Warn, 'Test warning log from store-frontend', testData);

      // Test error log
      CoralogixRum.log(CoralogixLogSeverity.Error, 'Test error log from store-frontend', testData);

      console.log('Coralogix test logs sent successfully', testData);

      // Monitor network requests to Coralogix (if PerformanceObserver is available)
      if (typeof PerformanceObserver !== 'undefined') {
        try {
          const observer = new PerformanceObserver((list) => {
            for (const entry of list.getEntries()) {
              if (entry.name.includes('coralogix')) {
                console.log('Coralogix network request:', entry);
              }
            }
          });
          observer.observe({ entryTypes: ['resource'] });
        } catch (observerError) {
          console.warn('PerformanceObserver not supported:', observerError);
        }
      }

      return true;
    } catch (error) {
      console.error('Failed to send test log to Coralogix:', error);
      return false;
    }
  };

  /**
   * Start a time measurement using Coralogix RUM's built-in timer
   * @param measureKey - Unique identifier for the measurement
   * @param labels - Optional labels to add context to the measurement
   */
  const startTimeMeasure = useCallback((measureKey: string, labels: Record<string, string | number> = {}) => {
    try {
      if (typeof CoralogixRum === 'undefined' || !CoralogixRum.startTimeMeasure) {
        console.warn('Coralogix RUM startTimeMeasure not available');
        return;
      }

      const playerData = webStorageUtil.get(ELocalStorageKeys.PLAYER_DATA);

      CoralogixRum.startTimeMeasure(measureKey, {
        playerId: playerData?.playerId || 'anonymous',
        playerCountry: playerData?.playerCountry || 'unknown',
        environment: getEnvVariable('REACT_APP_ENV', process.env.REACT_APP_ENV) || 'unknown',
        ...labels,
      });

      if (process.env.NODE_ENV === 'development') {
        console.log(`Time measure started: ${measureKey}`);
      }
    } catch (error) {
      console.error('Failed to start time measure:', error);
    }
  }, []);

  /**
   * End a time measurement using Coralogix RUM's built-in timer
   * @param measureKey - The same key used in startTimeMeasure
   */
  const endTimeMeasure = useCallback((measureKey: string) => {
    try {
      if (typeof CoralogixRum === 'undefined' || !CoralogixRum.endTimeMeasure) {
        console.warn('Coralogix RUM endTimeMeasure not available');
        return;
      }

      CoralogixRum.endTimeMeasure(measureKey);

      if (process.env.NODE_ENV === 'development') {
        console.log(`Time measure ended: ${measureKey}`);
      }
    } catch (error) {
      console.error('Failed to end time measure:', error);
    }
  }, []);

  return {
    logErrorToCoralogix,
    testCoralogixConnection,
    sendHealthLog,
    startHealthMonitoring,
    stopHealthMonitoring,
    startTimeMeasure,
    endTimeMeasure,
  };
}
